import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel } from './ui/UI';
/**
 * Home How-It-Works — the 5-step flow.
 *
 * Previously: 5 elaborate cards with lucide icons, per-step "signals"
 * sub-grid (sources / verdicts / paths / fix routes), TiltedCard hover,
 * accent tones per step. Read as a chunky, decorative AI feature grid.
 *
 * Now: 5 editorial entries with verb + sentence + supporting paragraph.
 * Same content beats, no icons, no card chrome, no signal sub-grid.
 */
const steps = [{
  n: '01',
  verb: 'Map.',
  title: 'Your assets, identities, applications, AI systems, and existing controls.',
  body: 'Periscan connects to cloud, identity, SaaS, code, AI apps, security tools, ticketing systems, and an optional internal runner. It builds a working evidence model of what matters: assets, identities, permissions, controls, exposures, applications, and remediation history.'
}, {
  n: '02',
  verb: 'Simulate.',
  title: 'Safe, real-world validation missions, run continuously.',
  body: 'Missions cover exposure, controls, attack paths, AI applications, and fixes. Testing is scoped, authorized, policy-controlled, rate-limited, and designed to avoid destructive behavior. Not a science fair.'
}, {
  n: '03',
  verb: 'Validate.',
  title: 'Prove whether defenses detect, block, log, alert, and respond.',
  body: 'Did EDR see it? Did SIEM log it? Did MFA stop it? Did the WAF block it? Did the SOC or MDR get the alert? Per-control verdicts, with evidence — not vibes.'
}, {
  n: '04',
  verb: 'Prioritize.',
  title: 'Focus remediation on paths with real business impact.',
  body: 'Periscan ranks what is reachable and exploitable, explains why each path matters, and recommends the fixes that close the most paths first. Not every "critical" deserves your weekend.'
}, {
  n: '05',
  verb: 'Prove.',
  title: 'Re-test fixes. Generate evidence you can share.',
  body: 'Closed tickets are no longer the finish line — closed paths are. If the risk is fixed, you get proof. If it is not, you get the truth. Evidence packs land for security teams, executives, customers, auditors, insurers, vCISOs, and MSSPs.'
}];
export function HomeHowItWorks() {
  return <section id="how-it-works" aria-labelledby="hiw-heading" className="relative w-full py-32 border-t border-white/5" data-id="element-955">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-956">
        <SectionLabel number="—" label="How Periscan delivers continuous validation" className="mb-8" data-id="element-957" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" data-id="element-958">
          <div className="lg:col-span-7" data-id="element-959">
            <motion.h2 id="hiw-heading" initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.02em] leading-[1.05] text-white" data-id="element-960">
              Map. Simulate. Validate.
              <br data-id="element-961" />
              <span className="text-white/40" data-id="element-962">Fix. Prove.</span>
            </motion.h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4" data-id="element-963">
            <p className="text-lg text-white/65 leading-relaxed" data-id="element-964">
              Five steps, run continuously — not once a year. The same loop a
              good red team would run, except the loop never stops and the
              evidence builds up over time.
            </p>
          </div>
        </div>

        <ol className="border-t border-white/5" data-id="element-965">
          {steps.map((s, i) => <motion.li key={s.n} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.55,
          delay: i * 0.05
        }} className="border-b border-white/5 py-12 grid grid-cols-12 gap-6 items-baseline" data-id="element-966">
              <span className="col-span-12 md:col-span-1 font-mono text-sm text-[#1a8fd4]/70 tracking-[0.2em]" data-id="element-967">
                {s.n}
              </span>
              <div className="col-span-12 md:col-span-4" data-id="element-968">
                <h3 className="text-3xl md:text-4xl text-white font-semibold tracking-tight leading-snug mb-2" data-id="element-969">
                  {s.verb}
                </h3>
                <p className="text-base text-white/55 leading-snug" data-id="element-970">
                  {s.title}
                </p>
              </div>
              <p className="col-span-12 md:col-span-7 text-base md:text-lg text-white/65 leading-relaxed max-w-2xl" data-id="element-971">
                {s.body}
              </p>
            </motion.li>)}
        </ol>
      </div>
    </section>;
}